import { PlayerExtended } from 'components/Player'
import { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  Ad,
  AdEvent,
  AdEventType,
  AdsLoader,
  AdsManager,
  AdsRequest
} from './google.ima'

export type Midroll = {
  tag: string
  offset: number
  prefetchOffset?: number
}

export const useAdManagerTracking = (
  playerInstance: PlayerExtended | null,
  onAdsManager?: (adsManager: AdsManager) => void,
  onAdsBreakStart?: () => void,
  onAdStart?: (ad: Ad | null) => void,
  onAdSkip?: () => void,
  onAdComplete?: () => void,
  onAdsBreakComplete?: () => void,
  midrolls?: Midroll[],
): void => {
  const [adsLoader, setAdsLoader] = useState<AdsLoader | undefined>()
  const [adsRequest, setAdsRequest] = useState<AdsRequest | undefined>()
  const [adsManagerGlobal, setAdsManager] = useState<AdsManager | undefined>()
  const [midrollFired, setMidrollFired] = useState(false)
  const [midrollList, setMidrollList] = useState(midrolls)
  const [lastFiredMidrollOffset, setLastFiredMidrollOffset] = useState(0)
  const [lastMidrollOffset, setLastMidrollOffset] = useState(
    (midrolls &&
      midrolls.length &&
      midrolls?.[midrolls?.length - 1].offset / 1000) ||
      undefined,
  )
  const currentTime =
    (!playerInstance?.isDisposed() && playerInstance?.currentTime()) || 0
  const nextMidroll = midrollList?.[0]

  const requestAds = useCallback(() => {
    if (adsLoader) {
      // Reset the IMA SDK.
      adsLoader.contentComplete()
    }
    if (!adsRequest || !adsLoader || !nextMidroll?.tag) {
      return
    }
    adsRequest.adTagUrl = nextMidroll.tag
    adsRequest.liveStreamPrefetchSeconds = nextMidroll?.prefetchOffset || 30
    adsLoader.requestAds(adsRequest)
  }, [adsLoader, adsRequest, nextMidroll])

  const playAds = useCallback(() => {
    requestAds()
    adsManagerGlobal?.start()
  }, [adsManagerGlobal, requestAds])

  useEffect(() => {
    const midrollSecOffset =
      nextMidroll && nextMidroll.offset && nextMidroll.offset / 1000

    if (midrollSecOffset && currentTime > midrollSecOffset + 60) {
      setMidrollList(midrollList.slice(1))
      setMidrollFired(false)
    }
    if (
      !midrollFired &&
      midrollSecOffset &&
      currentTime >= midrollSecOffset &&
      currentTime <= midrollSecOffset + 60
    ) {
      setMidrollFired(true)
      setLastFiredMidrollOffset(nextMidroll.offset)
      playAds()
    }
  }, [
    currentTime,
    lastMidrollOffset,
    midrollFired,
    midrollList,
    nextMidroll,
    playAds,
  ])

  const onLoadAdsManager = useCallback(
    (adsManager: { adsManager: AdsManager }) => {
      const adsManagerInstance = adsManager?.adsManager
      setAdsManager(adsManager?.adsManager)
      if (!adsManagerInstance) return
      onAdsBreakStart &&
        adsManagerInstance?.addEventListener(AdEventType.LOADED, () => {
          onAdsBreakStart()
        })
      onAdSkip &&
        adsManagerInstance.addEventListener(AdEventType.SKIPPED, () => {
          onAdSkip()
          onAdsBreakComplete?.()
        })
      const onAdStarted = (adEvent: AdEvent) => onAdStart?.(adEvent.getAd())
      onAdStart &&
        adsManagerInstance.addEventListener(AdEventType.STARTED, onAdStarted)
      onAdComplete &&
        adsManagerInstance.addEventListener(AdEventType.COMPLETE, () => {
          onAdComplete()
          onAdsBreakComplete?.()
        })

      onAdsManager?.(adsManagerInstance)
    },
    [
      onAdComplete,
      onAdSkip,
      onAdStart,
      onAdsBreakComplete,
      onAdsBreakStart,
      onAdsManager,
    ],
  )

  const onLoadAdsLoader = useCallback((response: { adsLoader: SetStateAction<AdsLoader | undefined> }) => {
    setAdsLoader(response?.adsLoader)
  }, [])

  const onLoadAdsRequest = useCallback((response: { AdsRequest: SetStateAction<AdsRequest | undefined> }) => {
    setAdsRequest(response?.AdsRequest)
  }, [])

  useEffect(() => {
    setMidrollList(filterFiredMidrolls(midrolls, lastFiredMidrollOffset))
    setMidrollFired(false)
    setLastMidrollOffset(
      (midrolls &&
        midrolls.length &&
        midrolls?.[midrolls?.length - 1].offset / 1000) ||
        undefined,
    )
  }, [midrolls])

  useEffect(() => {
    if (!playerInstance?.isDisposed?.()) {
      playerInstance?.on('ads-manager', onLoadAdsManager)
      playerInstance?.on('ads-loader', onLoadAdsLoader)
      playerInstance?.on('ads-request', onLoadAdsRequest)
    }

    return () => {
      playerInstance?.off('ads-manager', onLoadAdsManager)
      playerInstance?.off('ads-loader', onLoadAdsLoader)
      playerInstance?.off('ads-request', onLoadAdsRequest)
    }
  }, [playerInstance, onLoadAdsManager])
}

function filterFiredMidrolls(
  midrolls: Midroll[] | undefined,
  lastFiredMidrollOffset: number,
) {
  const midrollsLength = midrolls?.length || 0
  let start = 0
  for (let i = 0; i < midrollsLength; i++) {
    if (midrolls?.[i] && midrolls[i].offset <= lastFiredMidrollOffset) {
      start++
    }
  }
  return midrolls?.slice(start)
}
